import { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeContext = createContext({ theme: "light", setTheme: () => {}, toggle: () => {} });

const STORAGE_KEY = "innoaim-theme-v2"; // v2 resets any previously stored dark preference

function apply(theme) {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-theme", theme);
    // Also toggle Tailwind's `dark` class so any `dark:` utilities work in future redesigns
    if (theme === "dark") document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
}

export function ThemeProvider({ children }) {
    const [theme, setThemeState] = useState(() => {
        if (typeof window === "undefined") return "light";
        try { return localStorage.getItem(STORAGE_KEY) || "light"; } catch { return "light"; }
    });

    useEffect(() => { apply(theme); }, [theme]);

    const setTheme = useCallback((t) => {
        setThemeState(t);
        try { localStorage.setItem(STORAGE_KEY, t); } catch {}
    }, []);

    const toggle = useCallback(() => {
        setThemeState(prev => {
            const next = prev === "dark" ? "light" : "dark";
            try { localStorage.setItem(STORAGE_KEY, next); } catch {}
            return next;
        });
    }, []);

    return (
        <ThemeContext.Provider value={{ theme, setTheme, toggle }}>
            {children}
        </ThemeContext.Provider>
    );
}

export const useTheme = () => useContext(ThemeContext);
